import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Grid3X3, X, Flag } from "lucide-react";
import { API, errMsg } from "../lib/api";
import { QuestionPanel } from "../components/QuestionPanel";
import { QuestionDiscussion } from "../components/QuestionDiscussion";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Session() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({}); // qid -> {selected, feedback}
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [flags, setFlags] = useState({});
  const qStart = useRef(Date.now());

  useEffect(() => {
    const raw = sessionStorage.getItem(`pamind_session_${id}`);
    if (!raw) { toast.error("Session data not found — please start a new session"); navigate("/app/topics"); return; }
    const s = JSON.parse(raw);
    setSession(s);
    setFlags(Object.fromEntries((s.questions || []).map((q) => [q.id, !!q.flagged])));
  }, [id, navigate]);

  const toggleFlag = async (questionId) => {
    const isFlagged = !!flags[questionId];
    try {
      if (isFlagged) {
        await API.delete(`/questions/${questionId}/flag`);
        toast.success("Flag removed");
      } else {
        await API.post(`/questions/${questionId}/flag`, { reason: "Need to review" });
        toast.success("Question flagged for review");
      }
      setFlags((f) => ({ ...f, [questionId]: !isFlagged }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const questions = session?.questions || [];
  const q = questions[idx];
  const current = q ? answers[q.id] : null;
  const submitted = !!current;
  const isLast = idx === questions.length - 1;
  const answeredCount = Object.keys(answers).length;

  const select = (l) => { if (!submitted) setSelected(l); };

  const submit = async () => {
    if (!selected || submitted) return;
    setSubmitting(true);
    try {
      const { data } = await API.post(`/sessions/${id}/answer`, {
        question_id: q.id, selected, time_taken_ms: Date.now() - qStart.current,
      });
      setAnswers((a) => ({ ...a, [q.id]: { selected, feedback: session.mode === "quiz" ? data : null } }));
      if (session.mode === "test" && !isLast) {
        setTimeout(() => goTo(idx + 1), 150);
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setSubmitting(false); }
  };

  const goTo = (i) => {
    setIdx(i);
    setSelected(answers[questions[i].id]?.selected || null);
    qStart.current = Date.now();
    setNavOpen(false);
  };

  const next = async () => {
    if (isLast) {
      try {
        await API.post(`/sessions/${id}/finish`);
        navigate(`/app/session/${id}/results`);
      } catch (e) { toast.error(errMsg(e)); }
    } else {
      goTo(idx + 1);
    }
  };

  const nav = useMemo(() => questions.map((qq, i) => {
    const a = answers[qq.id];
    let cls = "bg-card border-border text-muted-foreground";
    if (a) {
      if (session.mode === "quiz") cls = a.feedback?.correct ? "bg-success text-success-foreground border-success" : "bg-destructive text-destructive-foreground border-destructive";
      else cls = "bg-primary text-primary-foreground border-primary";
    }
    if (i === idx) cls += " ring-2 ring-primary ring-offset-2";
    return { i, cls, answered: !!a, flagged: !!flags[qq.id] };
  }), [questions, answers, idx, session, flags]);

  if (!session || !q) return <div className="py-20 text-center text-muted-foreground text-sm">Loading session…</div>;

  const navigatorPanel = (
    <div className="grid grid-cols-5 gap-2" data-testid="question-navigator">
      {nav.map((n) => (
        <button key={n.i} data-testid={`nav-question-${n.i + 1}`} onClick={() => goTo(n.i)}
          aria-label={`Question ${n.i + 1}${n.answered ? ", answered" : ""}${n.flagged ? ", flagged" : ""}`}
          className={`relative w-9 h-9 rounded-md border text-sm font-semibold transition-colors ${n.cls}`}>
          {n.i + 1}
          {n.flagged && <Flag className="w-2.5 h-2.5 absolute -top-1 -right-1 text-primary fill-primary" data-testid={`nav-flag-${n.i + 1}`} />}
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="session-page" className="max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground capitalize" data-testid="session-mode-label">
            {session.mode} mode
          </span>
          <p className="text-sm text-muted-foreground mt-0.5">{answeredCount} of {questions.length} answered</p>
        </div>
        <div className="flex items-center gap-3">
          {session.mode === "test" && answeredCount > 0 && (
            <Button variant="outline" size="sm" data-testid="finish-test-early-btn"
              onClick={async () => { await API.post(`/sessions/${id}/finish`); navigate(`/app/session/${id}/results`); }}>
              Finish test
            </Button>
          )}
          <button data-testid="toggle-navigator-btn" onClick={() => setNavOpen(!navOpen)}
            className="lg:hidden flex items-center gap-1.5 text-sm font-medium border border-border rounded-md px-3 py-2">
            {navOpen ? <X className="w-4 h-4" /> : <Grid3X3 className="w-4 h-4" />} Questions
          </button>
        </div>
      </div>

      {navOpen && <div className="lg:hidden bg-card border border-border rounded-lg p-4 mb-6">{navigatorPanel}</div>}

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-9 bg-card border border-border rounded-lg p-6 md:p-10 shadow-sm">
          <QuestionPanel
            key={q.id}
            question={q} index={idx} total={questions.length}
            selected={selected} onSelect={select}
            submitted={submitted} feedback={current?.feedback}
            onSubmit={submit} onNext={next} isLast={isLast}
            submitting={submitting} mode={session.mode}
            flagged={!!flags[q.id]} onToggleFlag={() => toggleFlag(q.id)}
          />
        </div>
        <aside className="hidden lg:block lg:col-span-3">
          <div className="bg-card border border-border rounded-lg p-5 sticky top-8">
            <h3 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-4">Navigator</h3>
            {navigatorPanel}
            <div className="mt-5 space-y-1.5 text-xs text-muted-foreground">
              <p className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-card border border-border inline-block" /> Unanswered</p>
              {session.mode === "quiz" ? (
                <>
                  <p className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-success inline-block" /> Correct</p>
                  <p className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-destructive inline-block" /> Incorrect</p>
                </>
              ) : (
                <p className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-primary inline-block" /> Answered</p>
              )}
            </div>
          </div>
          {submitted && (
            <div className="mt-5">
              <QuestionDiscussion key={`d-${q.id}`} questionId={q.id} />
            </div>
          )}
        </aside>
      </div>

      {submitted && (
        <div className="lg:hidden mt-8">
          <QuestionDiscussion key={`m-${q.id}`} questionId={q.id} suffix="-mobile" />
        </div>
      )}
    </div>
  );
}
